import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  DEFAULT_DOCTOR_PATIENT_SORT,
  canOpenView,
  doctorPatientsExportName,
  doctorPatientsFilterNarrows,
  readDoctorPatientsFilter,
  writeDoctorPatientsFilter,
  type DoctorPatient,
  type DoctorPatientSort,
  type DoctorPatientsFilter,
  type SortPreset,
} from '@/domain';
import {
  CLIENT_CASES_PAGE_SIZE,
  useDoctorPatients,
  useExportDoctorPatients,
} from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { downloadBlob } from '@/shared/format/download';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { Pagination } from '@/ui/data/Pagination';
import { ResponsiveTable, type Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { PatientIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import type { CasesDoctor } from './ClientCases';
import { CountRow, ExportButton, LoadFailed, None } from './ClientTableParts';
import { asServed, columnOfSort, sortOfColumn } from './clientTables';
import { useAddressParams, useAddressSearch } from './useAddressParams';
import styles from './ClientCases.module.css';

/**
 * What the keys of this table are written under in the page's address. The
 * Cases tab beside it keeps its own search, order and page under the same
 * three names, so one of the two tables has to say which is whose.
 */
export const DOCTOR_PATIENTS_PREFIX = 'patients.';

/** The three orders the bar offers, which the server sorts by. By name is the server's own. */
const SORTS: readonly SortPreset<DoctorPatient>[] = [
  { key: 'name', label: strings.clientCases.sortName },
  { key: '-lastCase', label: strings.clientCases.sortLatestCase },
  { key: '-cases', label: strings.clientCases.sortMostCases },
];

/**
 * A doctor's patients: the Patients tab of the doctor's page. The request of
 * 6 October 2026 (fifth), section 4.
 *
 * Every patient with a case under this doctor, each with what their cases
 * WITH THIS DOCTOR come to: how many, how many still in hand, and when the
 * newest was booked. The patient's own record counts every doctor they have
 * been to, and is not what this table is about. Searched by name or ID,
 * narrowed to the patients with an active case here, ordered and paged by the
 * server, kept in the page's address, and exported.
 *
 * A row is the patient's cases with this doctor: pressing it goes to the Cases
 * tab narrowed to that patient, where the patient's name is the way to their
 * own page.
 *
 * A list of patients and not of cases, so it is the plain table and not the
 * case table, as the Patients screen is.
 */
export function DoctorPatients({
  doctor,
  onShowCases,
}: {
  doctor: CasesDoctor;
  /** A patient was pressed: the Cases tab, narrowed to that patient. */
  onShowCases: (patient: DoctorPatient) => void;
}) {
  const navigate = useNavigate();
  const views = useSession().data?.views ?? [];
  const [params, replace] = useAddressParams();
  const { pageSize, choose } = useTablePage('doctorPatients', CLIENT_CASES_PAGE_SIZE);
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('doctorPatients');

  const filter = useMemo<DoctorPatientsFilter>(
    () => ({ ...readDoctorPatientsFilter(params, DOCTOR_PATIENTS_PREFIX), pageSize: undefined }),
    [params],
  );
  const page = filter.page ?? 1;
  const sort = filter.sort ?? DEFAULT_DOCTOR_PATIENT_SORT;

  /** A change worked out from the address as it stands, and written back in place. */
  const write = useCallback(
    (next: (current: DoctorPatientsFilter) => DoctorPatientsFilter) =>
      replace((current) =>
        writeDoctorPatientsFilter(
          current,
          {
            ...next(readDoctorPatientsFilter(current, DOCTOR_PATIENTS_PREFIX)),
            pageSize: undefined,
          },
          DOCTOR_PATIENTS_PREFIX,
        ),
      ),
    [replace],
  );
  // Page three of the old list is nothing in the new one.
  const change = (changes: Partial<DoctorPatientsFilter>) =>
    write((current) => ({ ...current, ...changes, page: undefined }));

  // Always a page: asked with neither number the server answers every patient at once.
  const patients = useDoctorPatients(doctor.id, { ...filter, page, pageSize });
  const exporter = useExportDoctorPatients();
  const search = useAddressSearch(filter.q, (q) => change({ q }));

  const rows = patients.data?.items ?? [];
  const total = patients.data?.total ?? 0;
  const narrowed = doctorPatientsFilterNarrows(filter);
  const served = asServed(rows, (patient) => patient.id, sort.startsWith('-'));

  // A page past the end goes back to the first, as on the cases beside it.
  const pastTheEnd = patients.data !== undefined && rows.length === 0 && total > 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) write((current) => ({ ...current, page: undefined }));
  }, [pastTheEnd, write]);

  function reset() {
    search.clear();
    change({ q: undefined, active: undefined });
  }

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.clientCases.searchPatients,
      placeholder: strings.clientCases.searchPatientsPlaceholder,
      value: search.typed,
      onChange: search.type,
    },
    {
      // An active case WITH THIS DOCTOR: one in hand with another doctor does not count here.
      kind: 'toggle',
      label: strings.clientCases.activePatients,
      value: filter.active === true,
      onChange: (next) => change({ active: next ? true : undefined }),
    },
  ];

  const columns: Column<DoctorPatient>[] = [
    {
      key: 'code',
      label: strings.clientCases.colId,
      mono: true,
      nowrap: true,
      render: (patient) => patient.code,
    },
    {
      key: 'name',
      label: strings.clientCases.colPatient,
      sortable: true,
      sortValue: served,
      // A name may be in Arabic: it sets its own direction.
      render: (patient) => (
        <span className={styles.name} dir="auto">
          {patient.name}
        </span>
      ),
    },
    {
      key: 'cases',
      label: strings.clientCases.colCases,
      mono: true,
      sortable: true,
      sortValue: served,
      render: (patient) => patient.withDoctor.caseCount.toLocaleString(),
    },
    {
      key: 'active',
      label: strings.clientCases.colActive,
      mono: true,
      render: (patient) => patient.withDoctor.activeCaseCount.toLocaleString(),
    },
    {
      key: 'lastCase',
      label: strings.clientCases.colLastCase,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: served,
      render: (patient) =>
        patient.withDoctor.lastCaseAt === null ? <None /> : formatDay(patient.withDoctor.lastCaseAt),
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      // Every doctor the patient has been to, this one among them.
      key: 'doctors',
      label: strings.table.columns.doctors,
      mono: true,
      optional: true,
      render: (patient) => patient.doctorCount.toLocaleString(),
    },
    {
      key: 'added',
      label: strings.table.columns.added,
      mono: true,
      nowrap: true,
      optional: true,
      render: (patient) => formatDay(patient.createdAt),
    },
  ];

  const empty = narrowed ? (
    <EmptyState
      icon={<PatientIcon />}
      heading={strings.clientCases.noPatientMatch}
      detail={strings.clientCases.noPatientMatchDetail}
      action={<Button onClick={reset}>{strings.clientCases.reset}</Button>}
    />
  ) : (
    <EmptyState
      icon={<PatientIcon />}
      detail={strings.clients.noPatientsOfDoctor}
      // A patient is not added under a doctor: a case booked in is what ties the two.
      action={
        canOpenView(views, 'intake') ? (
          <Button onClick={() => void navigate('/intake')}>{strings.clientCases.bookCase}</Button>
        ) : undefined
      }
    />
  );

  return (
    <section>
      <FilterBar
        label={strings.clientCases.patientFiltersLabel}
        filters={filters}
        sort={
          <SortSelect
            presets={SORTS}
            // An order a header asked for that the bar does not offer reads By column.
            value={SORTS.some((preset) => preset.key === sort) ? sort : ''}
            onChange={(key) => change({ sort: key as DoctorPatientSort })}
          />
        }
      />

      {patients.isError && (
        <LoadFailed error={patients.error} onRetry={() => void patients.refetch()} />
      )}

      {patients.isPending && <SkeletonRows rows={3} label={strings.clients.loadingPatients} />}

      {!patients.isPending && !patients.isError && (
        <>
          <CountRow
            total={total}
            counted={strings.clientCases.patientsCounted(total)}
            narrowed={narrowed}
            onReset={reset}
            action={
              <ExportButton
                pending={exporter.isPending}
                onExport={() =>
                  exporter.mutate(
                    { doctorId: doctor.id, filter },
                    {
                      onSuccess: (blob) => {
                        downloadBlob(blob, doctorPatientsExportName(doctor.code));
                        toast(strings.clientCases.patientsExported(total));
                      },
                      onError: () => toast(strings.clientCases.exportFailed),
                    },
                  )
                }
              />
            }
          />

          {rows.length === 0 ? (
            !pastTheEnd && empty
          ) : (
            <>
              <ResponsiveTable
                customize={{ layout: layout.layout, save: layout.save, reset: layout.reset, rows: { size: pageSize, sizes: PAGE_SIZES, choose } }}
                columns={columns}
                rows={rows}
                rowKey={(patient) => patient.id}
                onRowClick={onShowCases}
                caption={strings.clientCases.patientsOf(doctor.name)}
                sortSelectOnMobile
                sort={columnOfSort(sort)}
                // Every header that orders asks the server: null is the phone's "Default order".
                onSortChange={(next) =>
                  change({
                    sort: next === null ? undefined : (sortOfColumn(next) as DoctorPatientSort),
                  })
                }
              />
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                sizes={PAGE_SIZES}
                onPage={(next) => write((current) => ({ ...current, page: next }))}
                onPageSize={(size) => {
                  choose(size);
                  write((current) => ({ ...current, page: undefined }));
                }}
                label={strings.table.pagesLabel}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
